import { DevelopmentError } from '../../../../webmodule-common/other/development-error';
import { FrameAttributeTemplate } from '../../../../webmodule-common/interop/interfaces/frame-data';
import { getBranchQuoteDefaultsForCurrentUser } from './v6/branch-quote-defaults';
import { QuoteProviderData } from '../../../quotes/data/quote-provider-data';
import { quoteSupplierProvider } from '../../../quotes/data/quoteSupplierProvider';
import { v6Util } from '../../../v6config/v6config';

export interface V6FranchiseeQuoteProviderData extends QuoteProviderData {
  defaultOptions?: unknown; // V6AttributeGroup[] | null;
  igu?: unknown; //V6QuoteIGU[] | null;
  iguHash?: { code: string; hash: string }[] | null;
  quoteCalculations?: unknown; //QCQuoteValues|null|undefined
  globalAttributesMasterSet?: unknown; //MasterAttributeVersion[] | null;
  quoteAttributeUpdateHistory?: unknown; //QuoteAttributeUpdateHistory[] | null;
  quoteAnnotations?: unknown; //V6QuoteAnnotation[] | null;
  quoteAttributeTemplates?: FrameAttributeTemplate[]; //V6AttributeTemplate[] | null;
}

// This should always be updated to provide the correct new item matching the version.
// any changes here should be reflected in a change of version
async function createV6QuoteProviderData(_supplierId: string): Promise<V6FranchiseeQuoteProviderData> {
  const obj: V6FranchiseeQuoteProviderData = {
    supplierType: quoteSupplierProvider.v6,
    version: v6QuoteDataProviderVersion,
    defaultOptions: [],
    quoteAnnotations: []
  };
  return obj;
}

//any time any changes are made to any part of the FranchiseeQuoteProviderData interface at any
//level then this version should be increased, and migration code should be added to take
//the object from one version to another.
export const v6QuoteDataProviderVersion = 3;

export async function createFranchiseeQuoteProviderData(
  supplierType: string,
  supplierId: string,
  quoteOwnerId: string
): Promise<QuoteProviderData> {
  if (supplierType !== quoteSupplierProvider.v6) throw new DevelopmentError(`Unknown supplier type`);
  const data = await createV6QuoteProviderData(supplierId);
  const branchDefaults = await getBranchQuoteDefaultsForCurrentUser(supplierId, quoteOwnerId);
  data.defaultOptions = branchDefaults ?? (await v6Util().getSupplierQuoteDefaults?.(supplierId)) ?? [];
  return data;
}

type EventProviderData = (data: QuoteProviderData) => Promise<QuoteProviderData>;

/**
 *
 * @param data data before migrations were used that we want to convert
 * @returns a proper quote provider data object
 */
async function v6ConvertToVersion1(data: QuoteProviderData): Promise<QuoteProviderData> {
  const version = data.version ?? 0;
  if (version !== 0) return data;
  //if we are here we are at a pre-version were the data isn't reallyu a quoteproviderdata
  const v: V6FranchiseeQuoteProviderData = {
    supplierType: quoteSupplierProvider.v6,
    version: 1,
    defaultOptions: (data as any).defaultOptions ?? null
  };
  return v;
}

async function v6ConvertToVersion2_AddIGU(data: QuoteProviderData): Promise<QuoteProviderData> {
  const version = data.version ?? 0;
  if (version !== 1) return data;
  const newData = data as V6FranchiseeQuoteProviderData;
  if (!newData.igu) newData.igu = [];
  return data;
}

//version 3 does not require any changes, but is here to ensure that the version is correct
const v6Upgrades: { ver: number; event: EventProviderData }[] = [
  { ver: 1, event: v6ConvertToVersion1 },
  { ver: 2, event: v6ConvertToVersion2_AddIGU }
];

/**
 * migrating of quote provider data.
 * @param data the data that might need migration
 * @returns
 */
async function upgradeV6(data: QuoteProviderData): Promise<QuoteProviderData> {
  for (let i = 0; i < v6Upgrades.length; i++) {
    const upgrade = v6Upgrades[i];
    const version = data.version ?? 0;
    if (upgrade.ver > version) data = await upgrade.event(data);
  }
  data.version = v6QuoteDataProviderVersion;
  return data;
}

/**
 *
 * @param data an object to check and convert and passthrough
 * @returns
 */
export async function validateAndUpdateFranchiseeQuoteProviderData(
  data: QuoteProviderData
): Promise<QuoteProviderData> {
  //Keep this at the very bottom of the file.
  const currentVersion = data.version ?? 0;
  const supplierType = data.supplierType ?? quoteSupplierProvider.v6;

  if (supplierType === quoteSupplierProvider.v6)
    if (currentVersion !== v6QuoteDataProviderVersion) {
      return await upgradeV6(data);
    } else return data;

  //add v7 stuff here
  return data;
}
